import { useAuthentication, useQueryParam, redactEmail, redactPhone } from '@psyomics/ui-common';
import { CodeCaptureForm, Header } from '@psyomics/ui-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useGetServiceUserQuery } from '../../account/hooks/use-get-service-user-query';
import { confirmVerificationCode, resendVerificationCode } from '../verification-client';

const VerificationCodeStep = ({
  deliveryAddress,
  onSubmit,
  onResend,
}: {
  readonly deliveryAddress: string;
  readonly onSubmit: (code: string) => Promise<void>;
  readonly onResend: () => void | Promise<void>;
}) => {
  const { t } = useTranslation();
  return (
    <div className="form-container space-y-6 pt-6">
      <Header level="h2">{t('auth.accountVerification.code.title')}</Header>
      <CodeCaptureForm
        autoComplete="one-time-code"
        buttonLabel={t('common.buttons.submit')}
        description={t('auth.accountVerification.code.sent', { to: deliveryAddress })}
        errorRequired={t('auth.common.errors.confirmationCodeMissing')}
        inputMode="numeric"
        onRetry={onResend}
        onSubmit={onSubmit}
        placeholder={'000000'}
        showSupportContact
      />
    </div>
  );
};

const AccountVerification = () => {
  const method = useQueryParam('method');
  const redirect = useQueryParam('redirect');
  const { apiUrl } = useAuthentication();
  const [serviceUser, fetching] = useGetServiceUserQuery();
  const navigate = useNavigate();

  if (method !== 'email' && method !== 'phone') throw new Error('Unexpected method');
  const handleCodeSubmit = async (code: string) => {
    try {
      await confirmVerificationCode(apiUrl, code, method);
      navigate(redirect ?? '/', { replace: true });
    } catch {
      throw new Error('mfa verification failed');
    }
  };

  const handleResend = async () => {
    await resendVerificationCode(apiUrl, method);
  };
  if (fetching) return null;
  const deliveryAddress = method === 'phone' ? serviceUser?.phoneNumber : serviceUser?.email;
  if (!deliveryAddress) throw new Error('no delivery address');
  const redactedAddress = method === 'phone' ? redactPhone(deliveryAddress) : redactEmail(deliveryAddress);

  return <VerificationCodeStep deliveryAddress={redactedAddress} onResend={handleResend} onSubmit={handleCodeSubmit} />;
};

export { AccountVerification };
